import { Controller } from '../utils/controller';
import { isMobile } from '../utils/ismobile';
import debounce from 'lodash/debounce';

export default class extends Controller {
  static outlets = ['search'];
  static targets = ['overlay', 'overlayBound', 'input', 'results', 'summary', 'searchInput'];
  static requiredTargets = [];

  static classes = ['hidden'];
  static requiredClasses = [];

  static values = {
    overlaytimeout: {
      type: Number,
      default: 1000
    },
    disableBodyOverflow: {
      type: Boolean,
      default: true
    }
  };

  // Class variables
  declare searchOverlayPosition: number | undefined;
  // OUTLETS
  declare searchOutlet;
  declare readonly hasSearchOutlet: boolean;
  // TARGETS
  declare overlayTarget: HTMLElement;
  declare overlayBoundTarget?: HTMLDivElement;
  declare readonly hasOverlayTarget: boolean;
  declare readonly hasOverlayBoundTarget: boolean;
  declare readonly hasSearchInputTarget: boolean;
  declare searchInputTarget: HTMLInputElement;

  // VALUES
  declare overlaytimeoutValue: number | undefined;
  declare disableBodyOverflowValue: boolean | undefined;

  // CLASSES
  declare hiddenClasses: string[];

  // LOCAL STATE
  private lastTimeout: number | undefined;
  private headerElement: HTMLHeadElement | null;

  initialize() {
    this.handleScroll = debounce(this.handleScroll, 50).bind(this);
    this.setOverlayPosition = this.setOverlayPosition.bind(this);
  }

  connect() {
    this.headerElement = document.querySelector('[data-behavior="site-header"]');
    if (this.headerElement) {
      document.addEventListener('scroll', this.handleScroll);
      this.headerElement.addEventListener('transitionend', this.setOverlayPosition);
    }
    this.getOverlayBoundElement();
  }

  getOverlayBoundElement() {
    if (this.hasSearchOutlet && this.searchOutlet.overlayBoundTarget && !isMobile()) {
      const rect = this.searchOutlet.overlayBoundTarget.getBoundingClientRect();
      this.searchOverlayPosition = rect?.bottom;
    }
  }
  handleScroll() {
    if (this.hasOverlayTarget && !this.overlayTarget.classList.contains('hidden')) {
      this.setOverlayPosition();
    }
  }

  setOverlayPosition() {
    const navbarSearchInput = document.querySelector('#navbar-search-input');
    if (this.hasOverlayTarget) {
      if (navbarSearchInput != null && isMobile()) {
        this.overlayTarget.style.top = '0px';
        return;
      }
      this.getOverlayBoundElement();
      if (
        this.searchOverlayPosition &&
        this.hasOverlayTarget &&
        (!this.overlayTarget.getAttribute('data-full-screen') || !isMobile())
      ) {
        this.overlayTarget.style.top =
          this.searchOverlayPosition > 0 ? this.searchOverlayPosition + 'px' : '0px';
      } else if (
        this.headerElement &&
        this.hasOverlayTarget &&
        (!this.overlayTarget.getAttribute('data-full-screen') || !isMobile())
      ) {
        const rect = this.headerElement.getBoundingClientRect();
        this.overlayTarget.style.top = rect.bottom + 'px';
      }
    }
  }

  handleMouseEnter() {
    if (isMobile()) {
      return false;
    }
    clearTimeout(this.lastTimeout);
    this.showOverlay();
    this.handleSearchOverlayDisplayedEvent();
  }

  handleMouseLeave() {
    if (isMobile()) {
      return false;
    }
    this.lastTimeout = setTimeout(() => {
      this.hideOverlay();
    }, this.overlaytimeoutValue);
  }

  toggleOverlay() {
    if (this.hasOverlayTarget && !this.overlayTarget.classList.contains(this.hiddenClasses[0])) {
      this.hideOverlay();
    } else {
      this.showOverlay();
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.focus();
      }
    }
  }

  showOverlay() {
    this.setOverlayPosition();
    if (this.hasOverlayTarget) {
      this.hiddenClasses.forEach((cls) => {
        this.overlayTarget.classList.remove(cls);
      });

      this.disableBodyOverflowValue && document.body.classList.add('overflow-hidden');
    }
  }

  hideOverlay() {
    if (this.hasOverlayTarget) {
      this.hiddenClasses.reverse().forEach((cls) => {
        this.overlayTarget.classList.add(cls);
      });
    }
    const body = document.body;
    this.disableBodyOverflowValue && body.classList.remove('overflow-hidden');
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = '';
    }
  }

  handleOverlayProductClick(event) {
    const overlayProductClickEvent = new CustomEvent('analytics.overlayProductClick', {
      detail: {
        productId: event.currentTarget.dataset.product_id
      }
    });
    window.dispatchEvent(overlayProductClickEvent);
  }

  handleSearchOverlayDisplayedEvent() {
    window.dispatchEvent(new CustomEvent('searchOverlayDisplayedEvent'));
  }

  handleSearchKeyPress(event) {
    const searchUrlPath = event.params.url || '/esearch?search';
    if (event.key === 'Enter' && searchUrlPath) {
      const searchTerm = event.target.value;
      window.location.href = `${searchUrlPath}=${searchTerm}`;
    }
  }

  // Used for typing directly into the navbar
  handleSearchInput(event) {
    const elem: HTMLInputElement | null = document.querySelector('#search');
    if (elem) {
      elem.value = event.detail.value;
      elem.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}
